/**
 * 数据适配器工具集
 * 合并 dataAdapters.ts + 部分 tts-utils.ts
 * 提供数据适配、转换、验证等功能
 * 
 * @author AI Assistant
 * @since 2025-01-12
 * @version 2.0.0
 */

import type { Task, TaskStatus } from '@/types/task.domain';
import { formatters } from '@/utils/formatters';
import { validators } from '@/utils/validators';
import { enableLog, formatPrefix } from '@/utils/app-runtime';

// ============= 类型定义 =============

export interface HistoryRecord {
  id: string;
  taskId: string;
  text: string;
  voice: string;
  audioUrl?: string;
  status: TaskStatus;
  createdAt: string;
  updatedAt: string;
  duration?: number;
  fileSize?: number;
  timestamp?: number;
  audioData?: string;
  storageMode?: string;
  engine?: string;
  format?: string;
}

export interface LocalTTSConfig {
  baseUrl: string;
  timeout?: number;
  defaultVoice?: string;
  defaultLanguage?: string;
  retryCount?: number;
  retryInterval?: number;
  defaultAudioFormat?: string;
  autoPlay?: boolean;
  enabled?: boolean;
}

export interface FreeLimitInfo {
  remaining: number;
  total: number;
  resetTime: string;
}

// ============= 数据适配器 =============

const MODULE_PREFIX = formatPrefix('DataAdapter');

/**
 * 适配历史记录数据（增强版）
 * 统一本地存储和云端API返回的数据结构，集成格式化和验证
 */
export const adaptHistoryRecord = (record: any): HistoryRecord => {
  try {
    // 数据验证
    if (!record) {
      if (enableLog) {
        console.warn(`${MODULE_PREFIX} 历史记录数据为空`);
      }
      throw new Error('历史记录数据不能为空');
    }

    // 基础数据适配
    const adapted: HistoryRecord = {
      id: record.id || record.requestId || String(Date.now() + Math.random()),
      text: record.text || '',
      voice: record.voice || record.voiceName || '',
      audioUrl: record.audioUrl || record.url || '',
      timestamp: record.timestamp || record.createdAt || Date.now(),
      status: normalizeTaskStatus(record.status || 'completed'),
      storageMode: record.storageMode || (record.userID ? 'cloud' : 'local'),
      fileSize: record.fileSize || record.file_size,
      duration: record.duration,
      engine: record.engine || 'default',
      format: record.format || 'mp3',
      taskId: record.taskId || record.task_id,
      audioData: record.audioData,
      createdAt: record.createdAt || new Date().toISOString(),
      updatedAt: record.updatedAt || new Date().toISOString()
    };

    // 数据格式化和验证
    if (adapted.text) {
      const textValidation = validators.text.validateTTSText(adapted.text);
      if (!textValidation.isValid) {
        if (enableLog) {
          console.warn(`${MODULE_PREFIX} 历史记录文本验证失败`, { text: adapted.text, error: textValidation.message });
        }
      }
    }

    if (adapted.voice) {
      const voiceValidation = validators.voice.validateVoice(adapted.voice);
      if (!voiceValidation.isValid) {
        if (enableLog) {
          console.warn(`${MODULE_PREFIX} 历史记录语音验证失败`, { voice: adapted.voice, error: voiceValidation.message });
        }
      }
    }

    // 时间戳标准化
    if (typeof adapted.timestamp === 'string') {
      adapted.timestamp = formatters.date.getMillisTimestamp(adapted.timestamp);
    }

    return adapted;
  } catch (error) {
    if (enableLog) {
      console.error(`${MODULE_PREFIX} 历史记录适配失败`, { record, error });
    }
    throw error;
  }
};

/**
 * 批量适配历史记录
 */
export const adaptHistoryRecords = (records: any[]): HistoryRecord[] => {
  if (!Array.isArray(records)) {
    if (enableLog) {
      console.warn(`${MODULE_PREFIX} 历史记录列表不是数组`, records);
    }
    return [];
  }

  return records
    .map((record, index) => {
      try {
        return adaptHistoryRecord(record);
      } catch (error) {
        if (enableLog) {
          console.error(`${MODULE_PREFIX} 第${index}条历史记录适配失败`, { record, error });
        }
        return null;
      }
    })
    .filter((record): record is HistoryRecord => record !== null);
};

/**
 * 转换为本地存储格式
 */
export const toLocalStorageFormat = (record: HistoryRecord): any => {
  return {
    id: record.id,
    text: record.text,
    voice: record.voice,
    audioUrl: record.audioUrl,
    timestamp: record.timestamp,
    status: record.status,
    storageMode: 'local',
    fileSize: record.fileSize,
    duration: record.duration,
    engine: record.engine,
    format: record.format,
    audioData: record.audioData
  };
};

/**
 * 适配任务数据
 */
export const adaptTask = (task: any): Task => {
  // 确保 id 是字符串类型（BaseEntity 要求）
  // taskId 是业务ID，用于API调用和路由
  const id = String(task.id || task.taskId || '');
  const taskId = task.taskId || task.task_id || '';

  // 从 segments[0] 获取语音信息
  const firstSegment = task.segments?.[0]
  const voiceFromSegment = firstSegment?.voice

  return {
    id,
    taskId,
    type: task.type || 'single',
    status: normalizeTaskStatus(task.status),
    title: task.title || task.text || task.content || '未命名任务',
    description: task.description,
    priority: task.priority || 'normal',
    progress: task.progress || 0,
    // 用户信息（必需字段）
    userId: task.userId || task.user_id || 0,
    guestId: task.guestId || task.guest_id,
    userTier: task.userTier || task.user_tier,
    // 业务关联字段
    errorMessage: task.errorMessage || task.error_message,
    parentTaskId: task.parentTaskId || task.parent_task_id,
    primaryAssetId: task.primaryAssetId || task.primary_asset_id,
    // 时间信息
    startedAt: task.startedAt || task.started_at,
    completedAt: task.completedAt || task.completed_at,
    estimatedTime: task.estimatedTime || task.estimated_time,
    remainingTime: task.remainingTime || task.remaining_time,
    // 元数据
    ipAddress: task.ipAddress || task.ip_address,
    fingerprint: task.fingerprint,
    source: task.source,
    referer: task.referer,
    config: {
      voice: task.voice || task.voice_name || voiceFromSegment,
      engine: task.engine || 'default',
      format: task.format || 'mp3',
      audioUrl: task.audio_url || task.audioUrl,
      duration: task.duration,
      fileSize: task.file_size || task.fileSize,
      text: task.text || task.content
    },
    metadata: task.metadata || {
      originalData: task,
      startedAt: task.started_at || task.startedAt,
      completedAt: task.completed_at || task.completedAt
    },
    error: task.error_message || task.errorMessage,
    createdAt: task.created_at || task.createdAt || new Date().toISOString(),
    updatedAt: task.updated_at || task.updatedAt || new Date().toISOString(),
    // 保留 items 数据（统一架构）
    items: task.items || [],
    // 批量任务统计字段（仅 type='batch' 时有值）
    totalItems: task.total_items || task.totalItems,
    completedItems: task.completed_items || task.completedItems,
    failedItems: task.failed_items || task.failedItems
  }
};

/**
 * 批量适配任务
 */
export const adaptTasks = (tasks: any[]): Task[] => {
  if (!Array.isArray(tasks)) return [];
  return tasks.map(adaptTask);
};

/**
 * 转换为API任务格式
 */
export const toApiTaskFormat = (task: Task): any => {
  // 从 items[0].segment 获取单次任务的文本和语音信息
  const firstItem = task.items?.[0]
  const segment = firstItem?.segment

  return {
    task_id: task.taskId,
    type: task.type,
    status: task.status,
    title: task.title,
    description: task.description,
    priority: task.priority,
    progress: task.progress,
    config: task.config,
    metadata: task.metadata,
    error_message: task.errorMessage,
    created_at: task.createdAt,
    updated_at: task.updatedAt,
    started_at: task.startedAt,
    completed_at: task.completedAt,
    // 从 segment 或 config 中提取 TTS 相关字段
    content: task.type === 'single' ? (segment?.text || task.config?.text) : undefined,
    voice_name: task.type === 'single' ? (segment?.voice || task.config?.voice) : undefined,
    audio_url: task.config?.audioUrl,
    duration: task.config?.duration,
    file_size: task.config?.fileSize,
    engine: task.config?.engine,
    format: task.config?.format
  };
};

/**
 * 标准化任务状态
 */
export const normalizeTaskStatus = (status: any): TaskStatus => {
  if (!status) return 'created';

  const statusStr = String(status).toLowerCase();
  const statusMap: Record<string, TaskStatus> = {
    'pending': 'created',
    'waiting': 'created',
    'created': 'created',
    'queued': 'queued',
    'processing': 'processing',
    'running': 'processing',
    'post_processing': 'post_processing',
    'completed': 'completed',
    'success': 'completed',
    'finished': 'completed',
    'failed': 'processing_failed',
    'error': 'processing_failed',
    'processing_failed': 'processing_failed',  // ✅ 处理阶段失败
    'post_failed': 'post_failed',        // ✅ 后处理阶段失败
    'cancelled': 'cancelled',
    'canceled': 'cancelled',
    'paused': 'cancelled', // 将 paused 映射为 cancelled
    'stopped': 'cancelled' // 将 stopped 映射为 cancelled
  };

  return statusMap[statusStr] || 'created';
};

// ============= 通用工具函数 =============

/**
 * 深度合并对象
 */
export const deepMerge = (target: any, source: any): any => {
  if (!source || typeof source !== 'object') return target;
  if (!target || typeof target !== 'object') return source;

  const result = { ...target };
  
  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
        result[key] = deepMerge(result[key], source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }
  
  return result;
};

/**
 * 安全获取嵌套属性值
 */
export const safeGet = (obj: any, path: string, defaultValue: any = undefined): any => {
  const keys = path.split('.')
  let result = obj
  
  for (const key of keys) {
    if (result === null || result === undefined || typeof result !== 'object') {
      return defaultValue
    }
    result = result[key]
  }
  
  return result !== undefined ? result : defaultValue
}

// ============= TTS 配置管理 =============

// 默认网络配置
const DEFAULT_NETWORK_CONFIG = {
  baseUrl: '/api',
  timeout: 30000,
  retryCount: 3,
  retryInterval: 1000
};

/**
 * 获取当前TTS配置
 */
export const getCurrentTTSConfig = (): LocalTTSConfig => {
  return {
    baseUrl: getBaseUrl(),
    defaultVoice: 'zh-CN-XiaoxiaoNeural',
    defaultLanguage: 'zh-CN',
    retryCount: DEFAULT_NETWORK_CONFIG.retryCount,
    retryInterval: DEFAULT_NETWORK_CONFIG.retryInterval,
    defaultAudioFormat: 'mp3',
    autoPlay: true,
    enabled: true
  };
};

/**
 * 获取基础URL
 */
export const getBaseUrl = (): string => {
  return localStorage.getItem('baseUrl') || DEFAULT_NETWORK_CONFIG.baseUrl;
};

/**
 * 设置基础URL
 */
export const setBaseUrl = (url: string): void => {
  localStorage.setItem('baseUrl', url);
};

// ============= TTS 配置 =============

/**
 * 默认本地TTS配置
 */
export const DEFAULT_LOCAL_TTS_CONFIG: LocalTTSConfig = {
  baseUrl: 'http://localhost:8080',
  timeout: 30000,
  defaultVoice: 'zh-CN-XiaoxiaoNeural',
  defaultLanguage: 'zh-CN',
  retryCount: 3,
  retryInterval: 1000,
  defaultAudioFormat: 'mp3',
  autoPlay: true,
  enabled: true
};

/**
 * 检查服务器连接
 */
export const checkServerConnection = async (): Promise<boolean> => {
  try {
    const baseUrl = getBaseUrl();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(`${baseUrl}/api/v1/health`, {
      method: 'GET',
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    console.warn('服务器连接检查失败:', error);
    return false;
  }
};

// ============= 导出工具集合 =============

export const dataAdapterUtils = {
  // 历史记录适配器
  adaptHistoryRecord,
  adaptHistoryRecords,
  toLocalStorageFormat,
  
  // 任务适配器
  adaptTask,
  adaptTasks,
  toApiTaskFormat,
  
  // 状态适配器
  normalizeTaskStatus,
  
  // 通用工具
  deepMerge,
  safeGet,
  
  // TTS配置
  getCurrentTTSConfig,
  getBaseUrl,
  setBaseUrl
};

export default dataAdapterUtils;